import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Row, Col, FlexView, Separator } from '@cuemath/comps';
import { Text } from '@cuemath/web-comps';
import { usePatchTicket, useGetCategoryTree, useGetUsers } from 'actions/support-agent';
import {
  ERROR_AUTO_CLEAR_MS,
  TICKET_STATUS,
  TICKET_SOURCE,
  RESOLVED_STATUSES,
} from '../constants';
import { buildCustomerTags, studentTags } from '../context-tags';
import {
  ContextContainer,
  ContextTag,
  ContextTagRow,
  CtxSection,
  CtxLabel,
  StudentHead,
  StudentAvatar,
  KVGrid,
  StatusBadge,
  CtxAction,
  SLARow,
  SLAItem,
  SA_COLORS,
  StatusDisplayBox,
  StatusEditBtn,
  StatusSelectorWrap,
  StatusOptionsWrap,
  StatusOptionBtn,
  StatusCancelBtn,
  BtnPrimary,
  BtnSecondary,
} from '../styled-comps';
import { copyToClipboard } from 'utils';
import Icon from './icon';
import CategoryTree from './category-tree';

// ── Local styled components ───────────────────────────────────────────

const StudentName = styled(Text)`
  && {
    font-size: 14px;
    font-weight: 600;
    color: ${SA_COLORS.TEXT_PRIMARY};
  }
`;

const StudentRole = styled(Text)`
  && {
    font-size: 12px;
    color: ${SA_COLORS.TEXT_TERTIARY};
  }
`;

const StudentInfo = styled(Col)`
  && {
    gap: 2px;
    min-width: 0;
  }
`;

const InvolvedHead = styled(FlexView)`
  gap: 6px;
  cursor: pointer;
  user-select: none;
`;

const CountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  background: ${SA_COLORS.BG_TERTIARY};
  padding: 1px 7px;
  border-radius: 10px;
`;

const InvolvedChev = styled(Icon)`
  margin-left: auto;
  transition: transform 0.15s ease;
  transform: ${({ $open }) => ($open ? 'rotate(180deg)' : 'none')};
`;

const StudentList = styled(FlexView)`
  gap: 8px;
  margin-top: 10px;
`;

const StudentCard = styled(FlexView)`
  border: 1px solid ${SA_COLORS.BORDER_TERTIARY};
  overflow: hidden;
`;

const StudentCardTop = styled(FlexView)`
  gap: 10px;
`;

const StudentBadgeAvatar = styled.span`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: ${SA_COLORS.GOLD_100};
  display: grid;
  place-items: center;
  flex-shrink: 0;
`;

const StudentGenericAvatar = styled(StudentBadgeAvatar)`
  background: ${SA_COLORS.BG_TERTIARY};
  color: ${SA_COLORS.TEXT_TERTIARY};
`;

const StudentIdentity = styled(FlexView)`
  min-width: 0;
  flex: 1;
`;

const NameText = styled(Text)`
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StudentCardFoot = styled(FlexView)`
  gap: 8px;
  background: ${SA_COLORS.BG_SECONDARY};
  border-top: 1px solid ${SA_COLORS.BORDER_TERTIARY};
`;

const IdText = styled(Text)`
  display: block;
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  user-select: all;
`;

const CopyIdBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
  color: ${({ $copied }) => ($copied ? SA_COLORS.GOLD_700 : SA_COLORS.TEXT_TERTIARY)};

  &:hover {
    color: ${SA_COLORS.TEXT_PRIMARY};
  }
`;

const InvolvedStudentCard = ({ student }) => {
  const [copied, setCopied] = useState(false);
  const copyTimer = useRef(null);
  const { student_id: studentId, name, grade } = student;
  const tags = studentTags(student);

  useEffect(() => () => clearTimeout(copyTimer.current), []);

  // copyToClipboard handles insecure/non-HTTPS contexts (execCommand fallback);
  // navigator.clipboard is undefined there and would throw synchronously.
  const handleCopy = useCallback(() => {
    copyToClipboard(studentId);
    setCopied(true);
    clearTimeout(copyTimer.current);
    copyTimer.current = setTimeout(() => setCopied(false), 1500);
  }, [studentId]);

  const initials = (name || '--').slice(0, 2).toUpperCase();
  const copyBtn = (
    <CopyIdBtn
      type="button"
      $copied={copied}
      onClick={handleCopy}
      title="Copy student ID"
    >
      <Icon name={copied ? 'check' : 'copy'} size={12} />
      <Text variant="body3" size={10} weight={600} color="currentColor">
        {copied ? 'Copied' : 'Copy'}
      </Text>
    </CopyIdBtn>
  );

  return (
    <StudentCard>
      <StudentCardTop flexDirection="row" alignItems="center" gap={10} gutter={12}>
        {name ? (
          <StudentBadgeAvatar>
            <Text variant="body3" size={11} weight={600} color={SA_COLORS.GOLD_700}>
              {initials}
            </Text>
          </StudentBadgeAvatar>
        ) : (
          <StudentGenericAvatar>
            <Icon name="user" size={14} />
          </StudentGenericAvatar>
        )}
        <StudentIdentity>
          <NameText
            variant="body2"
            size={name ? 13 : 12}
            weight={600}
            color={SA_COLORS.TEXT_PRIMARY}
          >
            {name || studentId}
          </NameText>
          {name && grade && (
            <Text variant="body3" size={11} color={SA_COLORS.TEXT_TERTIARY}>
              Grade {grade}
            </Text>
          )}
        </StudentIdentity>
        {tags.length > 0 && (
          <ContextTagRow>
            {tags.map(tag => (
              <ContextTag key={tag.tone} variant="body3" $tone={tag.tone}>
                {tag.label}
              </ContextTag>
            ))}
          </ContextTagRow>
        )}
        {!name && copyBtn}
      </StudentCardTop>
      {name && (
        <StudentCardFoot flexDirection="row" alignItems="center" gap={7} gutter={12}>
          <Text variant="body3" size={9.5} weight={600} color={SA_COLORS.TEXT_TERTIARY}>
            ID
          </Text>
          <IdText variant="body3" size={11} color={SA_COLORS.TEXT_PRIMARY}>
            {studentId}
          </IdText>
          {copyBtn}
        </StudentCardFoot>
      )}
    </StudentCard>
  );
};

// Opened when any student carries a chip: a signal living only on a collapsed
// row never reaches the agent, and Eval has no header aggregate to fall back on.
const hasTags = students => (students || []).some(s => studentTags(s).length > 0);

const InvolvedStudentsSection = ({ students }) => {
  const [open, setOpen] = useState(() => hasTags(students));
  const toggle = useCallback(() => setOpen(o => !o), []);

  if (!students || students.length === 0) return null;

  return (
    <CtxSection>
      <InvolvedHead flexDirection="row" alignItems="center" onClick={toggle}>
        <CtxLabel>Selected Students</CtxLabel>
        <CountBadge>
          <Text variant="body3" size={11} weight={600} color={SA_COLORS.TEXT_SECONDARY}>
            {students.length}
          </Text>
        </CountBadge>
        <InvolvedChev name="chev" size={12} $open={open} />
      </InvolvedHead>
      {open && (
        <StudentList>
          {students.map((s, i) => (
            <InvolvedStudentCard key={s.student_id || i} student={s} />
          ))}
        </StudentList>
      )}
    </CtxSection>
  );
};

const SourceSection = styled(CtxSection)`
  && {
    background: ${SA_COLORS.INFO_50};
  }
`;

const SourceTag = styled(Row)`
  && {
    align-items: center;
    gap: 6px;
    color: ${SA_COLORS.INFO_700};
  }
`;

const SourceTagText = styled(Text)`
  && {
    font-size: 14px;
    font-weight: 600;
    color: ${SA_COLORS.INFO_700};
  }
`;

const CreatedByRow = styled(Row)`
  && {
    align-items: baseline;
    gap: 12px;
  }
`;

const CreatedByLabel = styled(Text)`
  && {
    font-size: 13px;
    color: ${SA_COLORS.TEXT_TERTIARY};
    white-space: nowrap;
  }
`;

const CreatedByValue = styled(Text)`
  && {
    font-size: 13px;
    color: ${SA_COLORS.TEXT_PRIMARY};
  }
`;

const PatchErrorMsg = styled(Text)`
  && {
    font-size: 12px;
    color: ${SA_COLORS.ERROR_600};
    padding: 6px;
  }
`;

const ConfirmText = styled(Text)`
  && {
    font-size: 13px;
    color: ${SA_COLORS.TEXT_PRIMARY};
    line-height: 1.4;
  }
`;

const ConfirmActions = styled(Row)`
  && {
    gap: 8px;
    justify-content: flex-end;
  }
`;

const SLALabel = styled(Text)`
  && {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    color: ${SA_COLORS.TEXT_TERTIARY};
  }
`;

const SLAValue = styled(Text)`
  && {
    font-size: 14px;
    font-weight: 700;
    color: ${({ $breached }) =>
      $breached ? SA_COLORS.ERROR_600 : SA_COLORS.SUCCESS_700};
  }
`;

const SLAStatus = styled(Text)`
  && {
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    color: ${({ $breached }) =>
      $breached ? SA_COLORS.ERROR_600 : SA_COLORS.SUCCESS_700};
  }
`;

const PrevTicketCard = styled(Col)`
  && {
    gap: 6px;
    padding: 12px;
    border-bottom: 1px solid ${SA_COLORS.BORDER_TERTIARY};
    cursor: pointer;

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      background: ${SA_COLORS.BG_SECONDARY};
    }
  }
`;

const PrevTicketId = styled(Text)`
  && {
    font-size: 13px;
    font-weight: 600;
    color: ${SA_COLORS.TEXT_PRIMARY};
    word-break: break-all;
  }
`;

const PrevTicketSubject = styled(Text)`
  && {
    font-size: 13px;
    color: ${SA_COLORS.TEXT_PRIMARY};
  }
`;

const PrevTicketFooter = styled(Row)`
  && {
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-top: 2px;
  }
`;

const PrevTicketMeta = styled(Text)`
  && {
    font-size: 11px;
    color: ${SA_COLORS.TEXT_TERTIARY};
  }
`;

const LinkedThread = styled(Col)`
  && {
    position: relative;
  }
`;

const ChainRow = styled(Row)`
  && {
    flex: none;
    position: relative;
    z-index: 1;
    align-items: center;
    gap: 11px;
    padding: 7px 8px;
    margin: 0 -8px;
    cursor: ${({ $current }) => ($current ? 'default' : 'pointer')};
    background: ${({ $current }) => ($current ? SA_COLORS.INFO_50 : 'transparent')};

    &:hover {
      background: ${({ $current }) =>
        $current ? SA_COLORS.INFO_100 : SA_COLORS.BG_SECONDARY};
    }
  }
`;

const NodeRail = styled(Col)`
  && {
    flex: none;
    position: relative;
    width: 26px;
    align-items: center;
    justify-content: center;

    &::after {
      content: '';
      display: ${({ $last }) => ($last ? 'none' : 'block')};
      position: absolute;
      left: 50%;
      top: calc(100% + 1px);
      transform: translateX(-50%);
      width: 2px;
      height: 12px;
      background: ${SA_COLORS.BORDER_TERTIARY};
    }
  }
`;

const ChainNode = styled(Text)`
  && {
    flex: none;
    box-sizing: border-box;
    width: 26px;
    height: 26px;
    text-align: center;
    line-height: 22px;
    font-size: 12px;
    font-weight: 700;
    border: 2px solid ${SA_COLORS.BG_PRIMARY};
    box-shadow: 0 0 0 1px
      ${({ $current, $closed }) =>
        $current
          ? SA_COLORS.INFO_700
          : $closed
          ? SA_COLORS.BORDER_PRIMARY
          : SA_COLORS.SUCCESS_700};
    background: ${({ $current, $closed }) =>
      $current
        ? SA_COLORS.INFO_700
        : $closed
        ? SA_COLORS.BG_SECONDARY
        : SA_COLORS.SUCCESS_100};
    color: ${({ $current, $closed }) =>
      $current
        ? SA_COLORS.BG_PRIMARY
        : $closed
        ? SA_COLORS.TEXT_TERTIARY
        : SA_COLORS.SUCCESS_700};
  }
`;

const ChainId = styled(Text)`
  && {
    flex: 1 0 auto;
    white-space: nowrap;
    font-size: 13px;
    font-weight: 600;
    color: ${({ $current }) =>
      $current ? SA_COLORS.TEXT_PRIMARY : SA_COLORS.TEXT_SECONDARY};
  }
`;

const HerePill = styled(Text)`
  && {
    flex: none;
    line-height: 1;
    font-size: 9.5px;
    font-weight: 700;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    white-space: nowrap;
    color: ${SA_COLORS.INFO_700};
    background: ${SA_COLORS.INFO_100};
    padding: 3px 7px;
  }
`;

const ChainStatusLabel = styled(Text)`
  && {
    flex: none;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    white-space: nowrap;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.3px;
    color: ${({ $closed }) =>
      $closed ? SA_COLORS.TEXT_TERTIARY : SA_COLORS.SUCCESS_700};

    &::before {
      content: '';
      flex: none;
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: ${({ $closed }) =>
        $closed ? SA_COLORS.BORDER_PRIMARY : SA_COLORS.SUCCESS_700};
    }
  }
`;

const SHORT_MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const formatShortDate = ts => {
  if (!ts) return '';
  const d = new Date(ts * 1000);
  return `${d.getDate()} ${SHORT_MONTHS[d.getMonth()]}`;
};

const formatTicketSubject = t => {
  const parts = [t.category, t.subject || t.last_message_preview].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : 'Ticket';
};

const formatHandledBy = t => `handled by ${t.agent_name || 'Bot'}`;

const isClosedStatus = status =>
  RESOLVED_STATUSES.includes(String(status || '').toUpperCase());

const formatStatusLabel = status => {
  const s = String(status || '')
    .replace(/_/g, ' ')
    .toLowerCase();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
};

// ── SLA badge helpers ─────────────────────────────────────────────────
// Once the actual SLA event has fired (first_response_at / resolved_at)
// the clock freezes — we render a stable "Responded in Xm" / "Resolved
// in Xm" or "Breached (+Xm)" label, never a wall-clock countdown.
// Without this the due-at timer keeps ticking past zero into "Overdue"
// even on tickets answered on time.

const fmtHM = ms => {
  const hours = Math.floor(ms / 3600000);
  const mins = Math.floor((ms % 3600000) / 60000);
  return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
};

const formatTimeLeft = dueAt => {
  if (!dueAt) return '--';
  const diffMs = dueAt * 1000 - Date.now();
  return diffMs <= 0 ? 'Overdue' : fmtHM(diffMs);
};

const formatTimeSince = (endedAt, startedAt, verb) => {
  if (!endedAt || !startedAt) return verb;
  const diffMs = (endedAt - startedAt) * 1000;
  return diffMs < 0 ? verb : `${verb} in ${fmtHM(diffMs)}`;
};

// `actualAt` null → fall back to wall clock so the badge counts up live
// (re-renders are driven by the existing 30s tick in the component).
const formatBreachDelta = (actualAt, dueAt) => {
  const endSec = actualAt || Date.now() / 1000;
  const diffMs = (endSec - dueAt) * 1000;
  return diffMs <= 0 ? 'Breached' : `Breached (+${fmtHM(diffMs)})`;
};

// Maps a ticket's SLA inputs to the rendered badge (value / status /
// breach color). Mirrors the spec table in PR #487 — one return per row.
const getSLABadge = ({
  dueAt,
  actualAt,
  startedAt,
  doneVerb,
  isClosedWithoutResolve,
}) => {
  if (!dueAt) return { value: '—', status: '—', breached: false };
  if (isClosedWithoutResolve) return { value: '—', status: 'Closed', breached: false };

  if (actualAt) {
    const breached = actualAt > dueAt;
    return {
      value: breached
        ? formatBreachDelta(actualAt, dueAt)
        : formatTimeSince(actualAt, startedAt, doneVerb),
      status: doneVerb,
      breached,
    };
  }

  const breached = Date.now() / 1000 >= dueAt;
  return {
    value: breached ? formatBreachDelta(null, dueAt) : formatTimeLeft(dueAt),
    status: breached ? 'Over target' : 'On track',
    breached,
  };
};

// ── Context Panel component ───────────────────────────────────────────

const ContextPanel = ({
  activeTicket,
  onTransfer,
  onSendEmail,
  onTicketUpdated,
  onPickTicket,
  userRole,
}) => {
  const td = useMemo(() => activeTicket || {}, [activeTicket]);

  // Fetch category tree
  const {
    data: categoryTreeData,
    get: getCategoryTree,
    isProcessingFailed: categoryTreeFailed,
  } = useGetCategoryTree();
  useEffect(() => {
    getCategoryTree('tree');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const categoryTree = Array.isArray(categoryTreeData) ? categoryTreeData : [];

  // Fetch customer details + recent tickets
  const {
    data: userData,
    get: getCustomer,
    isProcessingFailed: userFailed,
  } = useGetUsers();
  const customerId = td.user_id;
  useEffect(() => {
    if (customerId) getCustomer('context-user', { query: customerId });
  }, [customerId]); // eslint-disable-line react-hooks/exhaustive-deps

  const users = userData?.users || [];
  const userDetail = customerId ? users.find(u => u.id === customerId) || null : null;
  const recentTickets = (userDetail?.tickets?.items || []).filter(t => t.id !== td.id);
  // Keyed on parent_ticket_id server-side, so it resolves even for email
  // tickets with no user in the system (unlike user-scoped Previous Tickets).
  const chainTickets = td.linked_chain?.tickets || [];

  // Force re-render every 30s so SLA formatTimeLeft stays current
  const [, setTick] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setTick(t => t + 1), 30000);
    return () => clearInterval(timer);
  }, []);

  const [ticketCategory, setTicketCategory] = useState(td.category || '');
  const [patchError, setPatchError] = useState(null);
  const [ticketStatus, setTicketStatus] = useState(td.status || 'Open');
  const [editingStatus, setEditingStatus] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);

  // Sync category + status when ticket changes
  useEffect(() => {
    const cat = td.category || '';
    setTicketCategory(cat);
    setTicketStatus(td.status || 'Open');
    setEditingStatus(false);
    setPendingStatus(null);
    setPatchError(null);
  }, [td, activeTicket]);

  // Auto-clear patch error after 5 seconds
  useEffect(() => {
    if (!patchError) return;
    const timer = setTimeout(() => setPatchError(null), ERROR_AUTO_CLEAR_MS);
    return () => clearTimeout(timer);
  }, [patchError]);

  // Track previous values for rollback on failure
  const prevValuesRef = useRef({ status: null, category: null });

  const perms = activeTicket?.permissions || {};

  // --- API hooks ---

  const { patch: patchTicket, isProcessing: ticketPatchProcessing } = usePatchTicket({
    onComplete: error => {
      if (error) {
        setPatchError(typeof error === 'string' ? error : 'Failed to update ticket');
        // Rollback optimistic updates
        if (prevValuesRef.current.status !== null) {
          setTicketStatus(prevValuesRef.current.status);
        }
        if (prevValuesRef.current.category !== null) {
          setTicketCategory(prevValuesRef.current.category);
        }
      } else {
        if (onTicketUpdated) onTicketUpdated();
      }
      prevValuesRef.current = { status: null, category: null };
    },
  });

  const handleStatusOptionClick = useCallback(option => {
    setPatchError(null);
    setPendingStatus(option);
  }, []);

  const handleCancelStatusChange = useCallback(() => {
    setPendingStatus(null);
  }, []);

  const handleConfirmStatusChange = useCallback(() => {
    if (!pendingStatus) return;
    const { value } = pendingStatus;
    prevValuesRef.current = { ...prevValuesRef.current, status: ticketStatus };
    setTicketStatus(value);
    setEditingStatus(false);
    setPendingStatus(null);
    const tId = td.id || activeTicket?.id;
    if (tId) {
      patchTicket(tId, { action: 'update_status', status: value });
    }
  }, [pendingStatus, td, activeTicket, patchTicket, ticketStatus]);

  // Status options from valid_transitions on the ticket
  const statusOptions = useMemo(() => {
    const transitions = activeTicket?.valid_transitions;
    if (!Array.isArray(transitions)) return [];
    return transitions.map(t => ({ label: t.name, value: t.value }));
  }, [activeTicket]);

  const handleCategoryChange = useCallback(
    value => {
      setPatchError(null);
      prevValuesRef.current = { ...prevValuesRef.current, category: ticketCategory };
      setTicketCategory(value);
      const tId = td.id || activeTicket?.id;
      if (tId) {
        patchTicket(tId, { action: 'update_disposition', category: value });
      }
    },
    [td, activeTicket, patchTicket, ticketCategory],
  );

  const isEmailOnly = activeTicket?.source === TICKET_SOURCE.EMAIL && !td.user_id;
  const cName =
    userDetail?.name || td.customer_name || (isEmailOnly ? td.email_from : 'Customer');
  const cInitials = (cName || '--').slice(0, 2).toUpperCase();
  const cRole =
    userDetail?.type || td.customer_user_type || (isEmailOnly ? 'Email sender' : '--');
  const customerTags = useMemo(
    () => buildCustomerTags(td.agent_signals, td.involved_students),
    [td.agent_signals, td.involved_students],
  );
  const cEmail = userDetail?.email || td.email_from || '--';
  const cPhone = userDetail?.phone || '--';
  const cUserId = userDetail?.id || '--';

  // SLA badges. Breach is computed live against `now` so row 3 ("no
  // response, deadline passed") flips and counts up without waiting for
  // a backend-flag refresh — the existing 30s tick drives re-renders.
  const frtBadge = getSLABadge({
    dueAt: td.first_response_due_at,
    actualAt: td.first_response_at,
    startedAt: td.handover_at,
    doneVerb: 'Responded',
  });
  const resBadge = getSLABadge({
    dueAt: td.resolution_due_at,
    actualAt: td.resolved_at,
    startedAt: td.handover_at,
    doneVerb: 'Resolved',
    // Edge: direct OPEN→CLOSED with no resolved_at (rare; bot-owned
    // auto-close has resolution_due_at=null and is row 1 already).
    isClosedWithoutResolve: !td.resolved_at && td.status === TICKET_STATUS.CLOSED,
  });

  return (
    <ContextContainer>
      {/* Customer Info */}
      <CtxSection>
        <CtxLabel>Customer</CtxLabel>
        <StudentHead>
          <StudentAvatar>{cInitials}</StudentAvatar>
          <StudentInfo>
            <StudentName>{cName}</StudentName>
            <ContextTagRow>
              <StudentRole>{cRole}</StudentRole>
              {customerTags.map(tag => (
                <ContextTag key={tag.tone} variant="body3" $tone={tag.tone}>
                  {tag.label}
                </ContextTag>
              ))}
            </ContextTagRow>
          </StudentInfo>
        </StudentHead>
        <Separator height={4} transparent />
        <KVGrid>
          <dt>Email</dt>
          <dd>{cEmail}</dd>
          <dt>Phone</dt>
          <dd>{cPhone}</dd>
          <dt>User ID</dt>
          <dd>{cUserId}</dd>
          {activeTicket?.source === TICKET_SOURCE.EMAIL && (
            <>
              <dt>Source</dt>
              <dd>Email</dd>
              {activeTicket.email_subject && (
                <>
                  <dt>Subject</dt>
                  <dd>{activeTicket.email_subject}</dd>
                </>
              )}
            </>
          )}
        </KVGrid>
      </CtxSection>

      <InvolvedStudentsSection key={td.id} students={td.involved_students} />

      {activeTicket?.source === TICKET_SOURCE.INTERNAL && (
        <SourceSection>
          <SourceTag>
            <Icon name="lock" size={14} />
            <SourceTagText>Internal ticket</SourceTagText>
          </SourceTag>
          {td.created_by_name && (
            <>
              <Separator height={8} transparent />
              <CreatedByRow>
                <CreatedByLabel>Created by</CreatedByLabel>
                <CreatedByValue>{td.created_by_name}</CreatedByValue>
              </CreatedByRow>
            </>
          )}
        </SourceSection>
      )}

      {patchError && <PatchErrorMsg>{patchError}</PatchErrorMsg>}

      {/* Status */}
      <CtxSection>
        <CtxLabel>Status</CtxLabel>
        {editingStatus && perms.can_change_status ? (
          <StatusSelectorWrap>
            {pendingStatus ? (
              <>
                <ConfirmText>
                  Change status to &quot;{pendingStatus.label}&quot;?
                </ConfirmText>
                <Separator height={8} transparent />
                <ConfirmActions>
                  <BtnSecondary onClick={handleCancelStatusChange}>Cancel</BtnSecondary>
                  <BtnPrimary
                    onClick={handleConfirmStatusChange}
                    disabled={ticketPatchProcessing}
                  >
                    {ticketPatchProcessing ? 'Updating…' : 'Confirm'}
                  </BtnPrimary>
                </ConfirmActions>
              </>
            ) : (
              <>
                <StatusOptionsWrap>
                  {statusOptions.map((opt, index) => (
                    <React.Fragment key={opt.value}>
                      {index > 0 && <Separator height={12} transparent />}
                      <StatusOptionBtn
                        $selected={opt.value === ticketStatus}
                        onClick={() => handleStatusOptionClick(opt)}
                      >
                        <StatusBadge $flex align="center" $status={opt.value}>
                          {opt.label}
                        </StatusBadge>
                      </StatusOptionBtn>
                    </React.Fragment>
                  ))}
                </StatusOptionsWrap>
                <StatusCancelBtn
                  onClick={() => {
                    setEditingStatus(false);
                    setPendingStatus(null);
                  }}
                >
                  Cancel
                </StatusCancelBtn>
              </>
            )}
          </StatusSelectorWrap>
        ) : (
          <StatusDisplayBox $status={ticketStatus}>
            <StatusBadge $flex $status={ticketStatus} $noBorder>
              {statusOptions.find(o => o.value === ticketStatus)?.label ||
                ticketStatus ||
                'Open'}
            </StatusBadge>
            {perms.can_change_status && statusOptions.length > 0 && (
              <StatusEditBtn onClick={() => setEditingStatus(true)}>
                <Icon name="setting" size={12} />
              </StatusEditBtn>
            )}
          </StatusDisplayBox>
        )}
      </CtxSection>

      {/* Category */}
      <CtxSection>
        <CtxLabel>Category</CtxLabel>
        {categoryTreeFailed ? (
          <PatchErrorMsg>Failed to load categories.</PatchErrorMsg>
        ) : (
          <CategoryTree
            selectedPath={ticketCategory}
            onSelect={perms.can_change_category ? handleCategoryChange : () => {}}
            categoryTree={categoryTree}
          />
        )}
      </CtxSection>

      {/* Quick Actions */}
      <CtxSection>
        <CtxLabel>Quick Actions</CtxLabel>
        {perms.can_transfer && (
          <CtxAction onClick={onTransfer}>
            <Icon name="handoff" size={16} />
            Transfer Ticket
          </CtxAction>
        )}
        <Separator height={8} transparent />
        <CtxAction onClick={onSendEmail}>
          <Icon name="send" size={16} />
          Send an email
        </CtxAction>
      </CtxSection>

      {/* SLA */}
      <CtxSection>
        <CtxLabel>SLA</CtxLabel>
        <SLARow>
          <SLAItem>
            <SLALabel>First Response</SLALabel>
            <SLAValue $breached={frtBadge.breached}>{frtBadge.value}</SLAValue>
            <SLAStatus $breached={frtBadge.breached}>{frtBadge.status}</SLAStatus>
          </SLAItem>
          <SLAItem>
            <SLALabel>Resolution</SLALabel>
            <SLAValue $breached={resBadge.breached}>{resBadge.value}</SLAValue>
            <SLAStatus $breached={resBadge.breached}>{resBadge.status}</SLAStatus>
          </SLAItem>
        </SLARow>
      </CtxSection>

      {/* Linked Thread */}
      {chainTickets.length > 0 && (
        <CtxSection>
          <CtxLabel>Linked Thread</CtxLabel>
          <LinkedThread gutter={0}>
            {chainTickets.map((t, index) => {
              const closed = isClosedStatus(t.status);
              return (
                <ChainRow
                  key={t.id}
                  $current={t.is_current}
                  onClick={t.is_current ? undefined : () => onPickTicket(t)}
                >
                  <NodeRail gutter={0} $last={index === chainTickets.length - 1}>
                    <ChainNode $current={t.is_current} $closed={closed}>
                      {index + 1}
                    </ChainNode>
                  </NodeRail>
                  <ChainId $current={t.is_current}>#{String(t.id).slice(0, 8)}</ChainId>
                  {t.is_current && <HerePill>You are here</HerePill>}
                  <ChainStatusLabel $closed={closed}>
                    {formatStatusLabel(t.status)}
                  </ChainStatusLabel>
                </ChainRow>
              );
            })}
          </LinkedThread>
        </CtxSection>
      )}

      {/* Previous Tickets */}
      <CtxSection>
        <CtxLabel>Previous Tickets</CtxLabel>
        {userFailed ? (
          <PatchErrorMsg>Failed to load customer details.</PatchErrorMsg>
        ) : recentTickets.length === 0 ? (
          <Text variant="caption">No previous tickets</Text>
        ) : (
          recentTickets.map(t => (
            <PrevTicketCard key={t.id} onClick={() => onPickTicket(t)}>
              <PrevTicketId>#{t.id}</PrevTicketId>
              <PrevTicketSubject>{formatTicketSubject(t)}</PrevTicketSubject>
              <PrevTicketFooter>
                <PrevTicketMeta>
                  {[formatShortDate(t.created_on), formatHandledBy(t)]
                    .filter(Boolean)
                    .join(' · ')}
                </PrevTicketMeta>
                <StatusBadge $status={t.status}>{t.status}</StatusBadge>
              </PrevTicketFooter>
            </PrevTicketCard>
          ))
        )}
      </CtxSection>
    </ContextContainer>
  );
};

export default ContextPanel;
